import { watch } from 'vue'

type PipeDataSource = { getData: (key: string) => unknown }

const isSet = (v: unknown): boolean => v !== undefined && v !== null && v !== ''

/**
 * Bounded wait for pipe data keys to become non-empty.
 *
 * Resolves `true` as soon as EVERY key in `keys` reads a non-empty value
 * (`undefined` / `null` / `''` count as unset), or `false` once `timeoutMs`
 * elapses. Watch-based (same pattern as `pipe.whenReady()` and the
 * `watch(() => pipe.getData(...))` calls in the pipeorders integration), so it
 * is agnostic to WHICH producer writes the value — a background order upsert,
 * an AC restore, or a status-event upsert all satisfy it the same way.
 *
 * Deliberately NOT tied to any integration's internal queue: consumers like
 * the checkout components only care that the data exists, not who wrote it.
 */
export function waitForPipeData(
  pipe: PipeDataSource,
  keys: string[],
  timeoutMs: number,
): Promise<boolean> {
  if (keys.every((k) => isSet(pipe.getData(k)))) return Promise.resolve(true)

  return new Promise<boolean>((resolve) => {
    let settled = false

    // Referenced before their declarations below — safe: neither callback can
    // fire synchronously (the watch has no `immediate`, setTimeout is async).
    const finish = (result: boolean) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      stop()
      resolve(result)
    }

    const stop = watch(
      () => keys.map((k) => pipe.getData(k)),
      (values) => {
        if (values.every(isSet)) finish(true)
      },
    )

    const timer = setTimeout(() => finish(false), timeoutMs)
  })
}
